import React from 'react';
import PropTypes from 'prop-types';

export default function Pick({ 
  pick_name,
  opponent_name,
  type,
  weight,
  american_odds,
  decimal_odds,
  supporting_strategies,
  total_strategies = 10,
  budget,
  showBudget = false
}) {
  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const calculateEstimatedWin = (weight, odds, budget) => {
    if (!budget) return 0;
    return budget * weight * odds;
  };

  return (
    <div className="pick-item">
      <div className={`pick-type ${type.toLowerCase()}`}>
        {type}
      </div>
      <div className="pick-details">
        <h3>{pick_name}</h3>
        <div className="opponent-line">
          <p className="vs">vs.</p>
          <p className="opponent">{opponent_name}</p>
        </div>
      </div>
      <div className="pick-stats">
        {showBudget ? (
          <div className="stat">
            <span>Bet:</span>
            <div className="stat-values">
              <strong>${formatCurrency(budget * weight)}</strong>
              <small>({(weight * 100).toFixed(1)}%)</small>
            </div>
          </div>
        ) : (
          <div className="stat">
            <span>Weight</span>
            <div className="stat-values">
              <strong>{(weight * 100).toFixed(1)}%</strong>
            </div>
          </div>
        )}
        <div className="stat">
          <span>Estimated Odds</span>
          <div className="stat-values">
            <strong>{american_odds}</strong>
            <small>({decimal_odds.toFixed(2)}x)</small>
          </div>
        </div>
        {showBudget && (
          <div className="stat">
            <span>Estimated Win:</span>
            <div className="stat-values">
              <strong>${formatCurrency(calculateEstimatedWin(weight, decimal_odds, budget))}</strong>
            </div>
          </div>
        )}
        <div className="stat">
          <span>Supporting Strategies</span>
          <div className="stat-values">
            <strong>{supporting_strategies} of {total_strategies}</strong>
          </div>
        </div>
      </div>

      <style jsx>{`
        .pick-item {
          position: relative;
          background: #181818;
          border-radius: var(--radius-md);
          padding: var(--spacing-lg);
        }

        .pick-item:hover {
          background: #1f1f1f;
        }

        .pick-type {
          position: absolute;
          top: var(--spacing-sm);
          right: var(--spacing-sm);
          padding: 4px 12px;
          border-radius: var(--radius-sm);
          font-size: 0.85rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .pick-type.moneyline {
          background: rgba(37, 99, 235, 0.2);
          color: #60a5fa;
        }

        .pick-type.parlay {
          background: rgba(147, 51, 234, 0.2);
          color: #c084fc;
        }

        .pick-details {
          margin-bottom: 12px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          gap: 2px;
          text-align: left;
          padding-right: 100px;
        }

        .pick-details h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          line-height: 1.2;
        }

        .opponent-line {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .opponent-line .vs {
          margin: 0;
          font-size: 13px;
          opacity: 0.6;
        }

        .opponent-line .opponent {
          margin: 0;
          font-size: 14px;
          opacity: 0.8;
          line-height: 1.2;
        }

        .pick-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: var(--spacing-md);
        }

        .pick-stats .stat {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .pick-stats .stat span {
          color: var(--text-secondary);
          font-size: 0.9rem;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .pick-stats .stat span::before {
          content: '';
          width: 16px;
          height: 16px;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          opacity: 0.7;
        }

        .pick-stats .stat:nth-child(1) span::before {
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>');
        }

        .pick-stats .stat:nth-child(2) span::before {
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24"><path d="M9 4H7v2H5v12h2v2h2v-2h2V6H9V4zm0 12H7V8h2v8zm10-8h-2V4h-2v4h-2v7h2v5h2v-5h2V8zm-2 5h-2v-3h2v3z"/></svg>');
        }

        .pick-stats .stat:nth-child(3) span::before {
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="white" stroke-width="2" viewBox="0 0 24 24"><path d="M3 3v18h18 M9 17v-8 M13 17V7 M17 17v-4"/></svg>');
        }

        .pick-stats .stat:nth-child(4) span::before {
          background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" fill="white" viewBox="0 0 24 24"><path d="M14 10H2v2h12v-2zm0-4H2v2h12V6zm4 8v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM2 16h8v-2H2v2z"/></svg>');
        }

        .pick-stats .stat-values {
          display: flex;
          align-items: baseline;
          gap: 4px;
        }

        .pick-stats .stat-values strong {
          color: var(--text);
          font-size: 1rem;
          font-weight: 600;
          padding-right: 4px;
        }

        .pick-stats .stat-values small {
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        @media (max-width: 768px) {
          .pick-item {
            position: relative;
            padding: 0;
            border-radius: 6px;
            overflow: hidden;
          }

          .pick-type {
            position: static;
            width: 100%;
            text-align: center;
            padding: 4px 0;
            margin: 0;
            font-size: 13px;
            font-weight: 500;
            border-radius: 0;
          }

          .pick-details {
            margin: 12px 16px;
            padding-right: 0;
          }

          .pick-stats {
            padding: 2px 12px 12px;
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .pick-stats .stat {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 4px;
            padding: 2px 0;
          }

          .pick-stats .stat:first-child {
            border: 1px solid rgba(255, 255, 255, 0.15);
            border-radius: 4px;
            padding: 6px 2px;
          }

          .pick-stats .stat span {
            font-size: 12px;
            opacity: 0.7;
            min-width: 80px;
            display: flex;
            align-items: center;
            gap: 4px;
            padding-left: 2px;
          }

          /* Match border color to pick type */
          .moneyline ~ .pick-details + .pick-stats .stat:first-child {
            border-color: rgba(66, 153, 225, 0.2);
          }

          .parlay ~ .pick-details + .pick-stats .stat:first-child {
            border-color: rgba(123, 73, 209, 0.2);
          }
        }
      `}</style>
    </div>
  );
}

Pick.propTypes = {
  pick_name: PropTypes.string.isRequired,
  opponent_name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['Moneyline', 'Parlay']).isRequired,
  weight: PropTypes.number.isRequired,
  american_odds: PropTypes.string.isRequired,
  decimal_odds: PropTypes.number.isRequired,
  supporting_strategies: PropTypes.number.isRequired,
  total_strategies: PropTypes.number,
  budget: PropTypes.number,
  showBudget: PropTypes.bool
}; 